import _clonedeep from 'lodash.clonedeep';
import * as Constants from '../constants';
import { verifyStatus } from '../helpers/customizedItem/customizedItem.helper';
import { mapSizesKey as sizesKeyMap } from '../models/item.model';
import { addItemAsModifiersPriceInTotalPrice } from '../redux/slices/itemCustomizationSlice';
import { toFixedNumber } from './helper';

export function calculateAdditionalPriceForItemsAsModifiers(
  items: { id: number; base: number; selectedItems: any[] }[]
) {
  let extraPrice = 0;
  items.forEach((row) => {
    extraPrice = row.selectedItems.reduce(
      (acc: number, sub: any) => acc + (sub?.additionalPrice ?? 0),
      extraPrice
    );
  });
  return extraPrice;
}

export function calculateAndUpdateTotalAdditionalPrice(
  itemsGroup: { id: number; base: number; selectedItems: any[] }[],
  extraPriceAction: '+' | '-',
  dispatch: (action: unknown) => void
): void {
  const extraPrice = calculateAdditionalPriceForItemsAsModifiers(itemsGroup);
  dispatch(
    addItemAsModifiersPriceInTotalPrice({ extraPrice, extraPriceAction })
  );
}

function sortThem(
  array: any[],
  item_counter_increment: number,
  order: { size: string },
  keyMap: typeof sizesKeyMap
) {
  const storage: Record<number, number> = {};
  array.forEach((val) => {
    if (
      val.isByDefault &&
      (storage[val.id]
        ? storage[val.id] <=
          item_counter_increment * val[keyMap[order.size]] -
            item_counter_increment
        : true)
    ) {
      storage[val.id] = storage[val.id]
        ? storage[val.id] + item_counter_increment
        : item_counter_increment;
      val.isGoodToGo = true;
    }
  });
  array.sort((a, b) => {
    if (a.isByDefault && a.isGoodToGo) {
      return -1;
    }
    return 1;
  });
  return array;
}

export function addAdditionalPrice({
  items,
  currentItemsGroup,
  order,
  relatedGroup = null,
}: {
  items: any[];
  currentItemsGroup: any;
  order: { size: string };
  relatedGroup?: number | null;
}) {
  const currentItemGroup = _clonedeep(currentItemsGroup);
  const newItemsAsModifiers = sortThem(
    [...items],
    currentItemsGroup.item_counter_increment,
    order,
    sizesKeyMap
  );
  if (relatedGroup) {
    for (let index = 0; index < newItemsAsModifiers.length; index++) {
      if (newItemsAsModifiers[index]?.isGoodToGo) {
        delete newItemsAsModifiers[index].isGoodToGo;
      }
      newItemsAsModifiers[index].additionalPrice = 0;
      newItemsAsModifiers[index].price = 0;
    }
    return newItemsAsModifiers;
  }
  for (let index = 0; index < newItemsAsModifiers.length; index++) {
    if (newItemsAsModifiers[index]?.isGoodToGo) {
      delete newItemsAsModifiers[index].isGoodToGo;
    }
    let itemAdditionalPrice = 0;

    let forLoopCount = 0;
    if (currentItemsGroup.item_counter_increment === 0) {
      forLoopCount = newItemsAsModifiers[index].quantity;
    } else {
      forLoopCount =
        newItemsAsModifiers[index].quantity /
        currentItemsGroup.item_counter_increment;
    }
    if (forLoopCount === 0) {
      newItemsAsModifiers[index].additionalPrice = itemAdditionalPrice;
    }
    for (let j = 0; j < forLoopCount; j++) {
      const price =
        newItemsAsModifiers[index]?.sub_item_price ??
        newItemsAsModifiers[index].price;
      currentItemGroup[sizesKeyMap.basePrice[order.size]] = toFixedNumber(
        currentItemGroup[sizesKeyMap.basePrice[order.size]] - price
      );
      if (currentItemGroup[sizesKeyMap.basePrice[order.size]] < 0) {
        itemAdditionalPrice = toFixedNumber(
          itemAdditionalPrice +
            Math.abs(currentItemGroup[sizesKeyMap.basePrice[order.size]])
        );
      }
      if (currentItemGroup[sizesKeyMap.basePrice[order.size]] <= 0) {
        currentItemGroup[sizesKeyMap.basePrice[order.size]] = 0;
      }
      newItemsAsModifiers[index].additionalPrice = itemAdditionalPrice;
    }
  }

  return newItemsAsModifiers;
}

export function byDefaultSeletedItems({
  currentItemGroup,
  items,
  mapSizesKey: sizeKey,
  order,
  currentlySelectedItemsAsModifier,
  parentItem,
}: {
  currentItemGroup: any;
  items: any;
  mapSizesKey: typeof sizesKeyMap;
  order: { size: string };
  currentlySelectedItemsAsModifier: any[];
  parentItem: any;
}) {
  const filteredItems = [...(currentItemGroup.items_as_modifier ?? [])];
  if (items) {
    const selectedItems: any[] = [];
    filteredItems
      .filter(
        (row) =>
          row.is_selected_with_count &&
          row[sizeKey[order.size]] &&
          verifyStatus(row, Constants.MODIFIER_LOCATION)
      )
      .forEach((row) => {
        const currentItemWithNewProperty = {
          ...row,
          quantity: currentItemGroup.item_counter_increment,
          actualQuantity:
            currentItemGroup.item_counter_increment * row[sizeKey[order.size]],
          isByDefault: true,
          additionalPrice: 0,
          isSelected: true,
        };
        for (let index = 0; index < row[sizeKey[order.size]]; index++) {
          selectedItems.push({ ...currentItemWithNewProperty });
        }
      });

    currentlySelectedItemsAsModifier.push({
      id: currentItemGroup.id,
      parentItem,
      base: currentItemGroup[sizeKey.basePrice[order.size]],
      parentIncrementCount: currentItemGroup.item_counter_increment,
      selectedItems,
      byDefaultSelectedItems: _clonedeep(selectedItems),
    });
  }
}

/**
 * Restore items-as-modifier selections from cart `sub_item_required_modifiers`
 * when editing. Rebuilds each selection directly from the saved cart modifier
 * fields so restoration is independent of the live menu payload and of the
 * `order.size` string (which differs between HALF/FULL trays).
 */
export function seedItemsAsModifierFromCart({
  groups,
  cartSubItemModifiers,
  order,
  parentItem,
}: {
  groups: any[];
  cartSubItemModifiers: any[];
  order: { size: string };
  parentItem: any;
}) {
  const result: any[] = [];
  const sizeKey = sizesKeyMap[order.size];

  groups.forEach((group) => {
    const groupMods = (cartSubItemModifiers ?? []).filter(
      (mod) => mod.modifier_group_id === group.id
    );
    if (groupMods.length === 0) {
      return;
    }

    const selectedItems: any[] = [];
    let parentIncrementCount = 0;
    const base = { price: 0, size_one_base: 0, size_two_base: 0 };

    groupMods.forEach((mod) => {
      if (!parentIncrementCount) {
        parentIncrementCount = mod.item_counter_increment;
        base.price = mod[sizesKeyMap.basePrice[order.size]];
        base.size_one_base = mod.size_one_base;
        base.size_two_base = mod.size_two_base;
      }

      const quantity = mod.quantity ?? 0;
      if (quantity <= 0 || !verifyStatus(mod, Constants.MODIFIER_LOCATION)) {
        return;
      }

      const increment = mod.item_counter_increment || 1;
      const template: any = {
        ...mod,
        id: mod.modifier_id,
        quantity: increment,
        isSelected: true,
        additionalPrice: 0,
      };

      let iterations = 0;
      if (mod.is_selected_with_count && (mod[sizeKey] ?? 0) > 0) {
        iterations =
          quantity <= increment ? increment / quantity : quantity / increment;
        template.isByDefault = true;
        template.actualQuantity = increment * mod[sizeKey];
      } else {
        iterations = quantity / increment;
      }

      for (let j = 0; j < iterations; j++) {
        selectedItems.push({ ...template });
      }
    });

    result.push({
      id: group.id,
      parentItem,
      base: base.price,
      parentIncrementCount,
      selectedItems: addAdditionalPrice({
        items: selectedItems,
        currentItemsGroup: {
          id: group.id,
          item_counter_increment: parentIncrementCount,
          size_one_base: base.size_one_base,
          size_two_base: base.size_two_base,
        },
        order,
      }),
      byDefaultSelectedItems: selectedItems.filter((it) => it.isByDefault),
    });
  });

  return result;
}
